import { useState, useCallback, useEffect } from 'react';
import { WaterLevelData, WaterLevelFilterState, WaterLevelFilterOptions } from '../types/waterLevel';
import { WaterLevelApiService } from '../services/waterLevelApi';
import { getTodayDateString, getDateNDaysAgo, getCurrentSeason } from '../utils/waterLevel';

export const useWaterLevel = () => {
    const [waterLevelData, setWaterLevelData] = useState<WaterLevelData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filterOptions, setFilterOptions] = useState<WaterLevelFilterOptions>({
        companies: [],
        estates: [],
        divisis: [],
        categories: [],
        wms_statuses: []
    });
    const [filters, setFilters] = useState<WaterLevelFilterState>({
        start_date: getDateNDaysAgo(7), // Default 7 days ago
        end_date: getTodayDateString(),
        category_id: 12, // Default to Water Level Block category
        dashboard_type: 'water_level',
        season: getCurrentSeason()
    });

    const fetchWaterLevelData = useCallback(async (filterParams?: WaterLevelFilterState) => {
        setLoading(true);
        setError(null);

        try {
            const filtersToUse = filterParams || filters;
            
            // Filter by category based on dashboard type or explicit category_id
            let categoryFilter = {};
            if (filtersToUse.category_id) {
                // Use explicit category_id if provided
                categoryFilter = { category_id: filtersToUse.category_id };
            } else if (filtersToUse.dashboard_type === 'curah_hujan') {
                categoryFilter = { category_id: 15 }; // Curah Hujan category ID
            } else {
                // Default to Water Level Block if no specific category
                categoryFilter = { category_id: 12 }; // Water Level Block category ID
            }

            const finalFilters = {
                ...filtersToUse,
                ...categoryFilter
            };

            const data = await WaterLevelApiService.getWaterLevelDashboard(finalFilters);
            setWaterLevelData(data);
            return data;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch water level data';
            setError(errorMessage);
            console.error('Error fetching water level data:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const options = await WaterLevelApiService.getFilterOptions();
            setFilterOptions(options);
        } catch (err) {
            console.error('Error fetching filter options:', err);
        }
    }, []);

    const updateFilters = useCallback((newFilters: WaterLevelFilterState) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    const clearFilters = useCallback(() => {
        const clearedFilters: WaterLevelFilterState = {
            start_date: getDateNDaysAgo(7),
            end_date: getTodayDateString(),
            category_id: 12, // Default to Water Level Block category
            dashboard_type: filters.dashboard_type || 'water_level'
        };
        setFilters(clearedFilters);
        return clearedFilters;
    }, [filters.dashboard_type]);

    // Load filter options on mount
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    return {
        waterLevelData,
        loading,
        error,
        filters,
        filterOptions,
        fetchWaterLevelData,
        updateFilters,
        clearFilters,
        fetchFilterOptions,
    };
};
